import React, { useCallback, useState } from 'react'
import {
  Bleed,
  Button,
  Checkbox,
  Paragraph,
  Row,
  useOverlay,
  useStatusOverlay,
  DialogOverlay,
  DialogOverlayActions,
  Separator,
} from '@fs/zion-ui'
import { NoticeImportant } from '@fs/zion-icon'
import Coverages from './coverages/Coverages'
import Metadata from './metadata/Metadata'
import {
  useChanges,
  useCheckboxes,
  useCoverages,
  useDetermineMode,
  useGroupData,
  useGroupDataForm,
  useGroupDataUpdate,
} from '../hooks'
import { DeleteCoveragesFooter } from '../components'
import { useOverlayContext } from '../../../../shared/providers/overlayContextProvider/OverlayContext'
import trans from './GroupData.transl'
import styles from './GroupData.styles'
import MetadataAuditHeader from '../../metadataAudit/MetadataAuditHeader'
import MetadataAuditInfoSheetFooter from '../../metadataAudit/MetadataAuditInfoSheetFooter'
import MarkMetadataCompleteDialog from '../components/MarkMetadatCompleteDialog/MarkMetadataCompleteDialog'

async function performSave({
  saveGroupData,
  showSaveGroupDataStatus,
  setEdited,
  coverageDataMissing,
  setAuditMissingCoveragesAsCompleted,
  showMissingCoverageCompleteStatus,
  setErrorList,
  setCoveragesError,
  closeOverlay,
  unsubscribe,
}) {
  let result
  try {
    result = await saveGroupData()
  } catch (e) {
    showSaveGroupDataStatus({
      message: trans.errorSaveMessage(),
      type: 'error',
      dismissable: true,
    })
    return
  }

  if (result?.code === '200') {
    if (coverageDataMissing) await setAuditMissingCoveragesAsCompleted(showMissingCoverageCompleteStatus)
    setCoveragesError(false)
    setEdited(true)
  } else {
    handleErrors(result, setErrorList, setCoveragesError, showSaveGroupDataStatus)
  }

  closeOverlay('editMetadataOverlay')
  unsubscribe(() => 'editPanelOpen')
}

function EditGroupFooter({
  showDeleteFooter,
  showMetadataAudit,
  handleSave,
  preSaveCheck,
  saving,
  setPreSaveCheck,
  cancelEditMode,
  saveButtonFunction,
}) {
  if (showDeleteFooter) return <DeleteCoveragesFooter />
  if (showMetadataAudit)
    return (
      <>
        <Separator size="xxl" />
        <MetadataAuditInfoSheetFooter saveCoverages={handleSave} />
      </>
    )
  return (
    <>
      <Separator size="xxl" />
      <Bleed sides>
        <div css={styles.infoSheetFooter}>
          <Checkbox
            name="I added this name just to stop errors in tests"
            label={trans.saveCheckbox()}
            checked={preSaveCheck}
            onChange={() => setPreSaveCheck(!preSaveCheck)}
          />
          <Row guttersY="none" alignX="end">
            <Button disabled={saving} onClick={cancelEditMode}>
              {trans.cancelButtonText()}
            </Button>
            <Button disabled={saving || !preSaveCheck} emphasis="high" onClick={saveButtonFunction}>
              {trans.saveButtonText()}
            </Button>
          </Row>
        </div>
      </Bleed>
    </>
  )
}

const EditGroupData = () => {
  const { deleteCoveragesMode, saveGroupData, setEdited } = useGroupDataForm()
  const dispatch = useGroupDataUpdate()
  const showSaveGroupDataStatus = useStatusOverlay()
  const showMissingCoverageCompleteStatus = useStatusOverlay()
  const { unsubscribe, closeOverlay } = useOverlayContext()
  const {
    group: { currentWorkflowActions = [], id: groupId },
  } = useGroupData()
  const { validateCoverages, coverageDataMissing, setAuditMissingCoveragesAsCompleted } = useCoverages()
  const { deleteMode } = useCheckboxes()
  const markMetadataCompleteOverlay = useOverlay()

  const handleValidate = useCallback(() => {
    const coverageError = validateCoverages()
    if (coverageError) {
      const message = coverageError
      showSaveGroupDataStatus({ message, type: 'error' })
    }
    return !coverageError
  }, [showSaveGroupDataStatus, validateCoverages])

  const [saving, setSaving] = useState(false)
  const [errorList, setErrorList] = useState([])
  const [coveragesError, setCoveragesError] = useState(null)
  const [preSaveCheck, setPreSaveCheck] = useState(false)
  const { showMetadataAudit } = useDetermineMode()
  const cancelMessageDialog = useOverlay({ initiallyOpen: false })
  const { getGroupHasBeenEdited } = useChanges({})

  const leaveEditMode = useCallback(() => {
    dispatch({ type: 'RESET', value: groupId })
    cancelMessageDialog.close()
    closeOverlay('editMetadataOverlay')
  }, [cancelMessageDialog, closeOverlay, dispatch, groupId])

  const cancelEditAction = useCallback(() => {
    if (getGroupHasBeenEdited()) cancelMessageDialog.open()
    else leaveEditMode()
  }, [cancelMessageDialog, getGroupHasBeenEdited, leaveEditMode])

  const handleSave = useCallback(() => {
    if (!handleValidate()) return
    setSaving(true)
    performSave({
      saveGroupData,
      showSaveGroupDataStatus,
      setEdited,
      coverageDataMissing,
      setAuditMissingCoveragesAsCompleted,
      showMissingCoverageCompleteStatus,
      setErrorList,
      setCoveragesError,
      closeOverlay,
      unsubscribe,
    }).finally(() => setSaving(false))
  }, [
    handleValidate,
    saveGroupData,
    setEdited,
    showSaveGroupDataStatus,
    showMissingCoverageCompleteStatus,
    closeOverlay,
    unsubscribe,
    coverageDataMissing,
    setAuditMissingCoveragesAsCompleted,
  ])

  const cancelEditMode = useCallback(() => {
    setErrorList([])
    setCoveragesError(false)
    cancelEditAction()
  }, [cancelEditAction])

  const showDeleteFooter = deleteCoveragesMode || deleteMode

  const saveButtonFunction = useCallback(() => {
    if (!handleValidate()) return

    const showCompleteMetadataOverlay =
      currentWorkflowActions?.length > 0
        ? currentWorkflowActions.find((wfa) => wfa.name === 'AuditCoverages' && wfa.status === 'Available')
        : null

    if (showCompleteMetadataOverlay) markMetadataCompleteOverlay.handleClick()
    else handleSave()
  }, [handleSave, markMetadataCompleteOverlay, handleValidate, currentWorkflowActions])

  return (
    <>
      {coveragesError && (
        <div css={styles.coveragesError}>
          <div css={styles.errorIcon}>
            <NoticeImportant />
          </div>
          <div css={styles.noBottomPadding}>
            <Paragraph size="sm">{trans.coverageErrorMessage()}</Paragraph>
          </div>
        </div>
      )}
      {showMetadataAudit && <MetadataAuditHeader hideEditButton />}
      <Coverages editable />
      <Metadata errors={errorList} editable />
      <EditGroupFooter
        showDeleteFooter={showDeleteFooter}
        showMetadataAudit={showMetadataAudit}
        handleSave={handleSave}
        preSaveCheck={preSaveCheck}
        saving={saving}
        setPreSaveCheck={setPreSaveCheck}
        cancelEditMode={cancelEditMode}
        saveButtonFunction={saveButtonFunction}
      />
      <DialogOverlay
        title={trans.unsavedChangesHeader()}
        {...cancelMessageDialog}
        footer={
          <DialogOverlayActions>
            <Button onClick={leaveEditMode}>{trans.leave()}</Button>
            <Button emphasis="high" onClick={cancelMessageDialog.close}>
              {trans.continueEditing()}
            </Button>
          </DialogOverlayActions>
        }
      >
        <Paragraph size="sm">{trans.areYouSure()}</Paragraph>
      </DialogOverlay>
      <MarkMetadataCompleteDialog overlay={markMetadataCompleteOverlay} handleSave={handleSave} />
    </>
  )
}

function handleErrors(result, setErrorList, setCoveragesError, showStatusOverlay) {
  const partialFailure = result.message.includes('The following fields experienced errors: ')

  const dividingError = result.message.split('errors:')
  if (dividingError.length > 1) {
    const allErrors = dividingError[1].split(',')
    setErrorList(allErrors)

    allErrors.forEach((err) => {
      if (err.trim() === 'imageViewerCoverage') setCoveragesError(true)
    })
  }

  showStatusOverlay({
    message: partialFailure ? trans.failureSaveMessage() : trans.errorSaveMessage(),
    type: 'error',
    dismissable: true,
  })
}

export default EditGroupData
